import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TenantStore } from '../../../stores/tenant.store';
import { AzupayClientStatus, OnboardingAzupayStatus } from '../../../types/tenant.types';
import {
  AzupayOnboardingEligibility,
  AzupayOnboardingPrefill,
  OnboardingAzupayRequest,
} from '../model/azupay-onboarding.models';
import { AzupayOnboardingService } from './azupay-onboarding.service';

@Injectable({ providedIn: 'root' })
export class AzupayOnboardingStore {
  private readonly service = inject(AzupayOnboardingService);
  private readonly tenantStore = inject(TenantStore);

  // === Existing signals ===
  private readonly _currentStep = signal(1);
  private readonly _showWizard = signal(false);
  private readonly _eligibility = signal<AzupayOnboardingEligibility>({
    clientId: null,
    hasNoBillingContracts: false,
  });
  private readonly _billingAcknowledged = signal(false);
  private readonly _oneWayAcknowledged = signal(false);
  private readonly _feeCollectionAcknowledged = signal(false);
  private readonly _termsAccepted = signal(false);
  private readonly _prefill = signal<AzupayOnboardingPrefill | null>(null);

  // === New signals for per-TLE flow ===
  private readonly _selectedTleId = signal<number | null>(null);
  private readonly _showOnboardingForm = signal(false);
  private readonly _showCompletion = signal(false);

  // === Existing computed signals (updated) ===
  readonly isForced = computed(
    () =>
      this.tenantStore.selectedTenant()?.onboardingAzupayStatus ===
      OnboardingAzupayStatus.InProgress,
  );
  readonly currentStep = computed(() => this._currentStep());
  readonly showWizard = computed(() => this._showWizard());
  readonly eligibility = computed(() => this._eligibility());
  readonly billingAcknowledged = this._billingAcknowledged.asReadonly();
  readonly oneWayAcknowledged = this._oneWayAcknowledged.asReadonly();
  readonly feeCollectionAcknowledged = this._feeCollectionAcknowledged.asReadonly();
  readonly termsAccepted = this._termsAccepted.asReadonly();
  readonly prefill = computed(() => this._prefill());

  // === New computed signals for TLE management ===
  readonly tenantLegalEntities = computed(
    () => this.tenantStore.selectedTenant()?.tenantLegalEntities ?? [],
  );

  readonly tlesNeedingOnboarding = computed(() =>
    this.tenantLegalEntities().filter(
      (tle) =>
        tle.azupayClientStatus === AzupayClientStatus.None ||
        tle.azupayClientStatus === AzupayClientStatus.Disabled,
    ),
  );

  readonly allTlesSubmitted = computed(
    () =>
      this.tenantLegalEntities().length > 0 &&
      this.tenantLegalEntities().every(
        (tle) =>
          tle.azupayClientStatus === AzupayClientStatus.Pending ||
          tle.azupayClientStatus === AzupayClientStatus.Enabled,
      ),
  );

  readonly hasUnprocessedTles = computed(() =>
    this.tenantLegalEntities().some(
      (tle) =>
        tle.azupayClientStatus === AzupayClientStatus.None ||
        tle.azupayClientStatus === AzupayClientStatus.Disabled,
    ),
  );

  readonly selectedTleId = computed(() => this._selectedTleId());
  readonly selectedTle = computed(
    () => this.tenantLegalEntities().find((tle) => tle.id === this._selectedTleId()) ?? null,
  );

  readonly showOnboardingForm = computed(() => this._showOnboardingForm() || this.isForced());
  readonly showCompletion = computed(() => this._showCompletion());

  /** Form cannot be closed until all TLEs are submitted (Pending/Enabled) */
  readonly isFormForced = computed(() => this.showOnboardingForm() && !this.allTlesSubmitted());

  // === Existing methods ===
  nextStep() {
    this._currentStep.update((step) => step + 1);
  }

  previousStep() {
    this._currentStep.update((step) => step - 1);
  }

  openWizard(step = 1): void {
    this._currentStep.set(step);
    this._showWizard.set(true);
  }

  closeWizard(): void {
    this._currentStep.set(1);
    this._showWizard.set(false);
    this._showOnboardingForm.set(false);
    this._selectedTleId.set(null);
    this._showCompletion.set(false);
    this._eligibility.set({
      clientId: null,
      hasNoBillingContracts: false,
    });
    this._billingAcknowledged.set(false);
    this._oneWayAcknowledged.set(false);
    this._feeCollectionAcknowledged.set(false);
    this._termsAccepted.set(false);
    this._prefill.set(null);
  }

  private get tenantId(): number | null {
    return this.tenantStore.selectedTenant()?.id || null;
  }

  async loadEligibility(): Promise<void> {
    const tenantId = this.tenantId;
    if (!tenantId) {
      throw new Error('Tenant ID is required to load eligibility');
    }

    const result = await firstValueFrom(this.service.getAzupayOnboardingEligibility(tenantId));
    this._eligibility.set(result);
  }

  setBillingAcknowledged(value: boolean): void {
    this._billingAcknowledged.set(value);
  }

  setOneWayAcknowledged(value: boolean): void {
    this._oneWayAcknowledged.set(value);
  }

  setFeeCollectionAcknowledged(value: boolean): void {
    this._feeCollectionAcknowledged.set(value);
  }

  setTermsAccepted(value: boolean): void {
    this._termsAccepted.set(value);
  }

  async confirmOnboarding(): Promise<void> {
    const tenantId = this.tenantId;
    if (!tenantId) {
      throw new Error('Tenant ID is required to confirm onboarding');
    }

    await firstValueFrom(this.service.confirmProcessOnboarding(this.tenantId));
  }

  // === Updated methods — per TLE ===
  async loadPrefill(): Promise<void> {
    const tenantId = this.tenantId;
    const tleId = this._selectedTleId();
    if (!tenantId || !tleId) {
      throw new Error('Tenant ID and TLE ID are required');
    }

    const result = await firstValueFrom(this.service.getAzupayOnboardingPrefill(tenantId, tleId));
    this._prefill.set(result);
  }

  async onboardAzupay(request: OnboardingAzupayRequest): Promise<void> {
    const tenantId = this.tenantId;
    const tleId = this._selectedTleId();
    if (!tenantId || !tleId) {
      throw new Error('Tenant ID and TLE ID are required');
    }
    await firstValueFrom(this.service.onboardAzupay(tenantId, tleId, request));
  }

  // === New methods for TLE selection ===
  selectTle(tleId: number): void {
    this._selectedTleId.set(tleId);
  }

  openOnboardingForm(): void {
    this._showOnboardingForm.set(true);
    const firstNeedingOnboarding = this.tlesNeedingOnboarding()[0];
    if (firstNeedingOnboarding) {
      this._selectedTleId.set(firstNeedingOnboarding.id);
    }
  }

  closeOnboardingForm(): void {
    this._showOnboardingForm.set(false);
    this._selectedTleId.set(null);
    this._prefill.set(null);
    this._showCompletion.set(false);
  }

  showCompletionScreen(): void {
    this._showCompletion.set(true);
  }

  closeCompletion(): void {
    this._showCompletion.set(false);
    this.closeOnboardingForm();
  }
}
